import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { X, Lock, Loader2, CreditCard, User, Mail, CheckCircle, Phone, MessageSquare, Send, Landmark } from 'lucide-react';
import axios from 'axios';

// --- CONFIGURAZIONE STRIPE ---
const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
const stripePromise = loadStripe(stripeKey);

interface BookingPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    listingId: string;
    checkIn: string;
    checkOut: string;
    guests: number;
    totalPrice?: number;
    currency?: string;
    coupon?: string;
    tag: string;
}

type PaymentMethodType = 'credit_card' | 'bank_transfer';

const CARD_SURCHARGE_PCT = 0.04;

const CheckoutForm: React.FC<BookingPaymentModalProps> = ({
                                                              onClose, listingId, checkIn, checkOut, guests, totalPrice, currency, coupon, tag
                                                          }) => {
    const stripe = useStripe();
    const elements = useElements();

    const isInstantBooking = tag === 'Direct Booking';

    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [acceptedTerms, setAcceptedTerms] = useState(false);

    // NUOVO: Stato per il metodo di pagamento
    const [paymentType, setPaymentType] = useState<PaymentMethodType>('credit_card');

    const [progress, setProgress] = useState(0);

    const cardSurchargeAmount = totalPrice ? totalPrice * CARD_SURCHARGE_PCT : 0;
    const cardFinalTotal = totalPrice ? totalPrice + cardSurchargeAmount : 0;

    // --- EFFETTO PER IL REDIRECT AUTOMATICO ---
    useEffect(() => {
        let interval: NodeJS.Timeout;
        let redirectTimeout: NodeJS.Timeout;

        if (success) {
            const totalDuration = 4000;
            const updateInterval = 40;
            const increment = 100 / (totalDuration / updateInterval);

            interval = setInterval(() => {
                setProgress((prev) => {
                    const next = prev + increment;
                    if (next >= 100) {
                        clearInterval(interval);
                        return 100;
                    }
                    return next;
                });
            }, updateInterval);

            redirectTimeout = setTimeout(() => {
                window.location.href = '/';
            }, totalDuration);
        }

        return () => {
            clearInterval(interval);
            clearTimeout(redirectTimeout);
        };
    }, [success]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Controllo extra di sicurezza per i termini
        if (!acceptedTerms) {
            setError("You must accept the Booking Agreement and Terms & Conditions.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let ccToken = null;

            // 1. Tokenizzazione Carta (Solo se è selezionata la carta di credito)
            if (paymentType === 'credit_card') {
                if (!stripe || !elements) {
                    throw new Error("Stripe non è ancora stato caricato.");
                }

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) throw new Error("Card element not found");

                const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement,
                    billing_details: {
                        name: `${formData.firstName} ${formData.lastName}`,
                        email: formData.email,
                        phone: formData.phone
                    }
                });

                if (stripeError) throw new Error(stripeError.message);
                ccToken = paymentMethod.id;
            }

            // 2. Preparazione Payload
            const basePayload = {
                listingId,
                checkIn,
                checkOut,
                guests,
                guestName: `${formData.firstName} ${formData.lastName}`,
                guestEmail: formData.email,
                guestPhone: formData.phone,
                ccToken: ccToken, // Sarà null se è stato scelto il bonifico
                coupon: coupon,
                paymentMethod: paymentType // Aggiunto per far sapere al backend la scelta
            };

            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
            let response;

            // 3. Logica di invio condizionale
            if (isInstantBooking) {
                response = await axios.post(`${baseUrl}/properties/create-reservation`, basePayload);
            } else {
                const inquiryPayload = { ...basePayload, message: message };
                response = await axios.post(`${baseUrl}/properties/create-inquiry`, inquiryPayload);
            }

            if (response.data && (response.data.success === true || response.status === 200)) {
                setSuccess(true);
            } else {
                throw new Error(response.data.message || "Operazione fallita lato server.");
            }

        } catch (err: any) {
            console.error("Booking/Inquiry Error:", err);
            const backendMessage = err.response?.data?.message || err.message || "Error.";
            setError(backendMessage);
        } finally {
            setLoading(false);
        }
    };

    // --- UI DI SUCCESSO ---
    if (success) {
        const radius = 40;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (progress / 100) * circumference;

        return (
            <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-500">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
                    {isInstantBooking ? (
                        <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                        <Send className="w-10 h-10 text-green-600 ml-1" />
                    )}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {isInstantBooking ? "Request Successful!" : "Request Sent!"}
                </h3>
                <p className="text-gray-500 mb-8 max-w-xs mx-auto leading-relaxed">
                    {isInstantBooking
                        ? (paymentType === 'bank_transfer' ? "Your reservation request is received. You will get an email with bank details to complete the payment." : "Your reservation has been confirmed. You will receive a confirmation email shortly.")
                        : "Your request has been sent to the owner. You will receive a notification as soon as it is approved."}
                </p>

                <div className="relative w-28 h-28 flex items-center justify-center mb-4">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r={radius} stroke="#e5e7eb" strokeWidth="8" fill="transparent" />
                        <circle
                            cx="50" cy="50" r={radius} stroke="#16a34a" strokeWidth="8" fill="transparent"
                            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round"
                            className="transition-all duration-75 ease-linear"
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-bold text-gray-800">{Math.round(progress)}%</span>
                    </div>
                </div>

                <p className="text-xs text-gray-400">Redirecting...</p>
            </div>
        );
    }

    // --- FORM COMPILAZIONE ---
    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4" /> Guest Details
                </h4>

                <div className="grid grid-cols-2 gap-4">
                    <input
                        type="text" required placeholder="First Name"
                        className="w-full border border-gray-200 p-3 rounded-lg bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand/20 transition"
                        value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    />
                    <input
                        type="text" required placeholder="Last Name"
                        className="w-full border border-gray-200 p-3 rounded-lg bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand/20 transition"
                        value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    />
                </div>

                <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                        type="email" required placeholder="Email Address"
                        className="w-full border border-gray-200 p-3 pl-10 rounded-lg bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand/20 transition"
                        value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    />
                </div>

                <div className="relative">
                    <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                    <input
                        type="tel" required placeholder="Phone Number (es. +39 333 1234567)"
                        className="w-full border border-gray-200 p-3 pl-10 rounded-lg bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand/20 transition"
                        value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    />
                </div>

                {!isInstantBooking && (
                    <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" /> Message to Host
                        </label>
                        <textarea
                            rows={3}
                            placeholder="Introduce yourself or ask a question..."
                            className="w-full border border-gray-200 p-3 rounded-lg bg-gray-50 focus:bg-white outline-none focus:ring-2 focus:ring-brand/20 transition resize-none"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                        />
                    </div>
                )}

                {/* CHECKBOX TERMINI E CONDIZIONI */}
                <div className="flex items-start gap-3 pt-2">
                    <input
                        type="checkbox"
                        id="terms-checkbox"
                        required
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer"
                    />
                    <label htmlFor="terms-checkbox" className="text-sm text-gray-600 leading-tight cursor-pointer">
                        I agree to the{' '}
                        <a href="/agreement" target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-medium hover:underline">
                            Booking Agreement
                        </a>{' '}
                        and the{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-medium hover:underline">
                            Terms & Conditions
                        </a>.
                    </label>
                </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-100">
                <h4 className="font-bold text-gray-900 flex items-center gap-2">
                    {paymentType === 'credit_card' ? <CreditCard className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
                    {isInstantBooking ? "Payment Details" : "Guarantee / Payment Method"}
                </h4>

                {/* SELEZIONE METODO DI PAGAMENTO */}
                <div className="grid grid-cols-2 gap-3 mb-2">
                    <button
                        type="button"
                        onClick={() => setPaymentType('credit_card')}
                        className={`p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition ${
                            paymentType === 'credit_card'
                                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        <CreditCard className="w-4 h-4" /> Credit Card
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                            paymentType === 'credit_card'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-500'
                        }`}>+4%</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setPaymentType('bank_transfer')}
                        className={`p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition ${
                            paymentType === 'bank_transfer'
                                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        <Landmark className="w-4 h-4" /> Bank Transfer
                    </button>
                </div>

                {/* VISUALIZZAZIONE DINAMICA IN BASE ALLA SCELTA */}
                {paymentType === 'credit_card' ? (
                    <div className="animate-in fade-in zoom-in-95 duration-200">
                        {!isInstantBooking && (
                            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100 mb-3">
                                Your card will not be charged now. It will be used as a guarantee and charged only if the owner accepts the request.
                            </p>
                        )}
                        <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded-lg border border-amber-100 mb-3">
                            A <strong>4% surcharge</strong> will be applied to credit card transactions.
                        </p>
                        {totalPrice ? (
                            <div className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-100 mb-3 space-y-1">
                                <div className="flex justify-between text-gray-600">
                                    <span>Subtotal</span>
                                    <span>{currency} {totalPrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Card surcharge (4%)</span>
                                    <span>{currency} {cardSurchargeAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-bold text-gray-900 pt-1 border-t border-gray-200">
                                    <span>Total</span>
                                    <span>{currency} {cardFinalTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        ) : null}
                        <div className="border border-gray-200 p-4 rounded-lg bg-white">
                            <CardElement options={{
                                style: {
                                    base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                                    invalid: { color: '#9e2146' },
                                },
                            }} />
                        </div>
                    </div>
                ) : (
                    <div className="animate-in fade-in zoom-in-95 duration-200">
                        <p className="text-sm text-gray-600 bg-blue-50/50 p-4 rounded-lg border border-blue-100 leading-relaxed">
                            You have selected <strong>Bank Transfer</strong>. By proceeding, you will receive an email with our bank details and instructions on how to complete the payment to secure your booking.
                        </p>
                    </div>
                )}
            </div>

            {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-start gap-2 animate-pulse">
                    <span className="mt-0.5">⚠️</span> {error}
                </div>
            )}

            <button
                type="submit"
                // Disabilita se mancano termini, se è in caricamento, oppure se è scelta la carta ma Stripe non è pronto
                disabled={!acceptedTerms || loading || (paymentType === 'credit_card' && !stripe)}
                className={`w-full text-white py-4 rounded-xl font-bold shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed ${
                    isInstantBooking ? "bg-gray-900 hover:bg-black" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
            >
                {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                    isInstantBooking
                        ? (paymentType === 'credit_card'
                            ? `Pay ${currency || ''} ${totalPrice ? cardFinalTotal.toFixed(2) : ''} & Book`
                            : `Request to Book via Transfer`)
                        : `Send Request`
                )}
            </button>

            <div className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> Data is secure and encrypted
            </div>
        </form>
    );
};

// Wrapper
const BookingPaymentModal: React.FC<BookingPaymentModalProps> = (props) => {
    if (!props.isOpen) return null;
    const isInstantBooking = props.tag === 'Direct Booking';

    return (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in zoom-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl relative flex flex-col max-h-[90vh]">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900 font-serif">
                        {isInstantBooking ? "Confirm Booking" : "Request to Book"}
                    </h3>
                    <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <Elements stripe={stripePromise}>
                        <CheckoutForm {...props} />
                    </Elements>
                </div>
            </div>
        </div>
    );
};

export default BookingPaymentModal;